<template>
  <div class="activity">
    <h2 class="section-title">{{ activity ? activity.title : 'Activité' }}</h2>
    <div v-if="hasFailed && !activity" class="notice">L'activité n'a pas pu être chargée : le container ne répond pas ou doit être redéployé avec la dernière version.</div>
    <div v-else-if="!activity" class="notice">Chargement…</div>
    <template v-else>
      <p class="note" :class="{memory: activity.storage === 'memory'}">{{ activity.subtitle }}</p>
      <div class="kpis">
        <StatisticsKpi v-for="kpi in activity.kpis" :key="kpi.label" :label="kpi.label" :value="cell(kpi.kind, kpi.value)" :hint="kpi.hint ?? ''" :tone="toneOf(kpi.tone)" />
      </div>
      <div class="charts">
        <StatisticsCard :span="12" :title="activity.chart.title" :empty="!hasChartData">
          <StatisticsChart :labels="activity.chart.labels" :datasets="chartDatasets" :stacked="activity.chart.stacked === true" value-format="integer" />
        </StatisticsCard>
        <StatisticsCard v-if="activity.breakdown" :span="12" :title="activity.breakdown.title">
          <ActivityTable :table="activity.breakdown" empty-text="Rien n'a été envoyé sur cette période." />
        </StatisticsCard>
        <StatisticsCard :span="12" :title="activity.entries.title">
          <div v-if="activity.filters && activity.filters.length" class="filters">
            <select v-for="filter in activity.filters" :key="filter.key" v-model="selected[filter.key]" :aria-label="filter.label" @change="load">
              <option v-for="option in filter.options" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <ActivityTable :table="activity.entries" empty-text="Aucune ligne ne correspond." />
        </StatisticsCard>
      </div>
    </template>
  </div>
</template>

<script>
import StatisticsKpi from "@/statistics/components/StatisticsKpi.vue";
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import ActivityTable from "@/administration/components/ActivityTable.vue";
import {hasData} from "@/statistics/utils/format";
import {AdministrationController} from "@/administration/controllers/AdministrationController";
import {cell} from "@/administration/utils";

// Teinte d'une série (tone) → couleur du graphique
const TONE_COLORS = {bad: '#ea4e2c', warn: '#ef9400'};

/**
 * Ce qu'un container a fait (mails envoyés, documents générés) : chiffres clés, graphique par jour, détail par type et derniers événements
 * filtrables. Le container décrit lui-même ce qu'il montre (/ms/activity).
 * containerId : container interrogé ; refreshedAt : change quand la page est actualisée, ce qui recharge aussi l'activité.
 */
export default {
  name: 'ContainerActivity',
  components: {StatisticsKpi, StatisticsCard, StatisticsChart, ActivityTable},
  props: {
    containerId: {type: String, required: true},
    refreshedAt: {type: String, default: ''},
  },
  data() {
    return {
      activity: undefined,
      selected: {},
      hasFailed: false,
      requestId: 0,
    };
  },
  computed: {
    chartDatasets() {
      return this.activity.chart.datasets.map(({tone, ...dataset}) => (TONE_COLORS[tone] ? {...dataset, color: TONE_COLORS[tone]} : dataset));
    },
    hasChartData() {
      return this.activity.chart.datasets.some(dataset => hasData(dataset.data));
    },
  },
  watch: {
    refreshedAt() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    cell,
    toneOf(tone) {
      return tone === 'warn' || tone === 'bad' ? tone : '';
    },
    async load() {
      // A late answer of an old request must not replace the last one
      const requestId = ++this.requestId;
      const resp = await AdministrationController.getContainerActivity(this.containerId, this.selected);
      if (requestId !== this.requestId) return;
      this.hasFailed = !resp || !!resp.errors;
      if (this.hasFailed) return;
      // The selects start on their first option (« all »)
      for (const filter of resp.filters ?? []) {
        if (!(filter.key in this.selected)) this.selected[filter.key] = '';
      }
      this.activity = resp;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/statistics/style/statistics.styl';

.activity
  margin-top 6px

.section-title
  margin 0 0 8px
  font-size 17px
  color THEME_COLOR_PRIMARY

.note
  margin 0 0 14px
  padding 8px 12px
  font-size 12.5px
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border-radius 10px

  &.memory
    color #8a5a00
    background #fff6e0

.notice
  padding 28px 16px
  text-align center
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border 1px dashed CTRL_BORDER
  border-radius 12px

.filters
  display flex
  flex-wrap wrap
  gap 10px
  margin-bottom 12px

  select
    height 38px
    padding 0 10px
    font inherit
    font-size 13.5px
    color #3f3931
    background white
    border 1px solid CTRL_BORDER
    border-radius 8px
</style>
